import { useCallback } from 'react';
import { Organization, TimeZone } from 'types';
import { organizationSettingsService } from 'core/API/organizationSettings';
import {
	GetOrganizationsParams,
	CreateOrganizationRequest,
	UpdateOrganizationRequest,
	ApiDayPart,
} from 'types/Organization';
import useSettingsStore from 'store/useSettingsStore';

interface UseOrganizationCrudState {
	organizations: Organization[];
	selectedOrganization: Organization | null;
	loading: boolean;
	error: string | null;
	meta: {
		total: number;
		page: number;
		limit: number;
	};
	organisationTypes: { id: number; name: string }[];
	brands: { label: string }[];
	timeZones: TimeZone[];
	days: {
		'@id': string;
		'@type': string;
		id: number;
		label: string;
		locale: string;
		position: string;
	}[];
	dayParts: ApiDayPart[];
}

interface UseOrganizationCrudActions {
	fetchOrganizations: (
		params?: GetOrganizationsParams,
		options?: { force?: boolean }
	) => Promise<void>;
	fetchOrganization: (organizationId: number) => Promise<Organization | null>;
	fetchOrganisationTypes: () => Promise<void>;
	fetchBrands: () => Promise<void>;
	fetchTimeZones: () => Promise<void>;
	fetchDays: () => Promise<void>;
	fetchDayParts: (organizationId?: number, options?: { force?: boolean }) => Promise<void>;
	createOrganization: (orgData: CreateOrganizationRequest) => Promise<Organization | null>;
	updateOrganization: (orgData: UpdateOrganizationRequest) => Promise<Organization | null>;
	deleteOrganization: (organizationId: number) => Promise<void>;
	createDayPart: (
		organizationId: number,
		dayPartData: { partName: string; startTime: string; endTime: string }
	) => Promise<ApiDayPart | null>;
	updateDayPart: (
		dayPartId: number,
		dayPartData: { organization: string; partName: string; startTime: string; endTime: string }
	) => Promise<ApiDayPart | null>;
	deleteDayPart: (dayPartId: number) => Promise<void>;
	setOrganizations: (organizations: Organization[]) => void;
	setSelectedOrganization: (organization: Organization | null) => void;
	clearError: () => void;
	resetState: () => void;
}

export interface UseOrganizationCrudReturn
	extends UseOrganizationCrudState,
		UseOrganizationCrudActions {}

/**
 * Provides organisation CRUD + lookup data, backed by the shared settings store.
 */
export const useOrganizationCrud = (): UseOrganizationCrudReturn => {
	const organizations = useSettingsStore((s) => s.organizations);
	const selectedOrganization = useSettingsStore((s) => s.selectedOrganization);
	const loading = useSettingsStore((s) => s.organizationsLoading);
	const error = useSettingsStore((s) => s.organizationsError);
	const meta = useSettingsStore((s) => s.organizationsMeta);
	const organisationTypes = useSettingsStore((s) => s.organisationTypes);
	const brands = useSettingsStore((s) => s.brands);
	const timeZones = useSettingsStore((s) => s.timeZones);
	const days = useSettingsStore((s) => s.days);
	const dayParts = useSettingsStore((s) => s.dayParts);

	const fetchOrganizations = useSettingsStore((s) => s.fetchOrganizations);
	const createOrganization = useSettingsStore((s) => s.createOrganization);
	const updateOrganization = useSettingsStore((s) => s.updateOrganization);
	const deleteOrganization = useSettingsStore((s) => s.deleteOrganization);
	const setSelectedOrganization = useSettingsStore((s) => s.setSelectedOrganization);
	const fetchOrganisationTypes = useSettingsStore((s) => s.fetchOrganisationTypes);
	const fetchBrands = useSettingsStore((s) => s.fetchBrands);
	const fetchTimeZones = useSettingsStore((s) => s.fetchTimeZones);
	const fetchDays = useSettingsStore((s) => s.fetchDays);
	const fetchDayParts = useSettingsStore((s) => s.fetchDayParts);
	const createDayPart = useSettingsStore((s) => s.createDayPart);
	const updateDayPart = useSettingsStore((s) => s.updateDayPart);
	const deleteDayPart = useSettingsStore((s) => s.deleteDayPart);

	const clearError = useCallback(() => {
		useSettingsStore.setState({ organizationsError: null });
	}, []);

	const setOrganizations = useCallback((next: Organization[]) => {
		useSettingsStore.setState({
			organizations: next?.[0] ? [next[0]] : [],
			organizationsLoaded: true,
		});
	}, []);

	const resetState = useCallback(() => {
		// Intentionally no-op — shared cache should persist across remounts.
	}, []);

	const fetchOrganization = useCallback(
		async (organizationId: number): Promise<Organization | null> => {
			const existing = useSettingsStore
				.getState()
				.organizations.find((o) => o.id === organizationId);
			if (existing) {
				setSelectedOrganization(existing);
				return existing;
			}
			try {
				const organization = await organizationSettingsService.getOrganization(organizationId);
				setSelectedOrganization(organization);
				return organization;
			} catch (err: any) {
				useSettingsStore.setState({
					organizationsError: err?.message || 'Failed to fetch organization',
				});
				return null;
			}
		},
		[setSelectedOrganization]
	);

	return {
		organizations,
		selectedOrganization,
		loading,
		error,
		meta,
		organisationTypes,
		brands,
		timeZones,
		days,
		dayParts,
		fetchOrganizations,
		fetchOrganization,
		fetchOrganisationTypes,
		fetchBrands,
		fetchTimeZones,
		fetchDays,
		fetchDayParts,
		createOrganization,
		updateOrganization,
		deleteOrganization,
		createDayPart,
		updateDayPart,
		deleteDayPart,
		setOrganizations,
		setSelectedOrganization,
		clearError,
		resetState,
	};
};
